import React, { useContext, useState, useEffect, useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { Link as A } from '@chakra-ui/react';
import { useRouter } from 'hooks/useRouter';
import { Sidebar, NavBar, Modal, ChevronRightIcon, PoweredByCincel } from '@design-system';
import {
  tokenName,
  tokenV3Name,
  expirationName,
  userName,
  cincelSession,
  activityName,
} from 'config/constants';
import { resolveTokenV3, getLegacyToken, isGuestContext, getLastActivity, getStorage, hasGuestOtpSession } from 'helpers/sessionContext';
import { resetInactivityTimeout } from 'utils/session';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useQuery } from 'react-query';
import { useClient } from 'hooks/useClient';
import { useSession } from 'store/session';
import { useToast } from '@chakra-ui/react';
import { convertSize } from 'utils/convertSize';
import { use100vh } from 'react-div-100vh';
import { WLContext } from '@contexts';
import { useSentry } from 'hooks/useSentry';
import { useTeams } from 'hooks/useTeams';
import classNames from 'classnames';
import { translations } from 'translations';
import { useTranslation, Trans } from 'react-i18next';

import meta from '../../meta.json';

import { Link } from 'react-router-dom';
import { GuestLoginModal } from '../../components/modules/Sidebar/components/GuestLoginModal';
import { clearSession } from 'helpers/sessionContext';
import { logger } from 'helpers/logger';


const fetchAccountLimits = async (client, organizationId) => {
  return client.accountLimits(organizationId);
};

const fetchAccountSubscription = async (client) => {
  const subscription = await client.accountSubscription();

  if (!subscription.ok)
    return {
      ok: false,
      errors: {
        error: 'No fue posible obtener la suscripción',
      },
    };

  const suscriptions = subscription.payload;
  const procesedSuscriptions: any = [];
  let currentActivePlan: any = '';
  for (const i in suscriptions) {
    const processedPlan = {
      name: suscriptions[i].subscription_plan.plan_product.name,
      status: suscriptions[i].status,
      type: suscriptions[i].subscription_plan.type,
      cancelled: suscriptions[i].cancel_at_period_end ? true : false,
      start_date: suscriptions[i].current_period_start_date,
      end_date: suscriptions[i].current_period_end_date,
    };
    if (
      processedPlan.status == 'active' &&
      processedPlan.name != 'extra_user' &&
      processedPlan.name != 'storage'
    ) {
      currentActivePlan = processedPlan;
    }
    procesedSuscriptions.push(processedPlan);
  }

  return {
    procesedSuscriptions,
    currentActivePlan,
  };
};

export default function AdminLayout({ className = '', children, logo }: any) {
  const { color, lightLogo } = useContext(WLContext);
  const { captureException } = useSentry();
  const { t } = useTranslation();

  const {
    user,
    currentOrganization,
    setCurrentPlan,
    setAccountLimits,
    clean,
    setShowBasicCertificates,
    setTeams,
    team,
    setCredits,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    isV3Expired,
    setIsV3Expired,
    idleTimeout,
  } = useSession();

  const client = useClient({
    url: process.env.REACT_APP_API,
    token: getLegacyToken() as string,
  });

  const clientV3 = useClient({
    url: process.env.REACT_APP_API_V3,
    token: resolveTokenV3() as string,
  });

  const toast = useToast();
  const height = use100vh();
  const router = useRouter();

  const guestUser = useMemo(() => {
    if (user) return null;
    const searchParams = new URLSearchParams(router.location.search);
    const token = searchParams.get('token');
    if (!token) return null;
    try {
      const decoded: any = jwtDecode(token);
      if (decoded && decoded.email) {
        return { email: decoded.email, name: decoded.email.split('@')[0] };
      }
    } catch {
      return null;
    }
    return null;
  }, [user, router.location.search]);

  const { userTeams } = useTeams({ id: user?.id, enabled: user !== null });

  const hasLegacyToken = !!getLegacyToken();

  const {
    data: accountSubscription,
    isError,
    error,
  } = useQuery<any>(
    ['accountSubscription'],
    () => fetchAccountSubscription(client),
    { enabled: user !== null && hasLegacyToken, retry: false },
  );

  const {
    data: accountLimits,
    isError: isLimitsError,
    error: limitsError,
    refetch: refetchAccountLimits,
  } = useQuery(
    ['limits'],
    () => fetchAccountLimits(client, (currentOrganization as any).id),
    {
      enabled: user !== null && hasLegacyToken,
      retry: false,
    },
  );

  const { refetch: refetchCredits } = useQuery<any>(
    ['fetch-team-credits', team?.uuid],
    async () => {
      const { data } = await clientV3.fetchTeamCredits(team?.uuid);
      setCredits(data);
    },
    { enabled: user !== null, retry: false },
  );

  // Idle Timeout Logic — Skip entirely for guests who haven't OTP-unlocked
  useEffect(() => {
    // 1. Guest tabs that haven't logged in via OTP should NEVER be idle-locked
    if (isGuestContext() && !hasGuestOtpSession()) {
      logger.log('Idle timeout skipped: user is a guest without OTP session');
      return;
    }

    // 2. Regular check for logged in user
    if (!user || isV3Expired) {
      if (!user) logger.log('Idle timeout inactive: no user session found');
      return;
    }

    logger.log('Idle timeout active', { timeout: idleTimeout });

    const IDLE_TIMEOUT = idleTimeout * 1000;
    const CHECK_INTERVAL = 1 * 1000; // 1 seconds

    const updateActivity = () => {
      resetInactivityTimeout();
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, updateActivity));

    // Initial activity set on mount (mark user as active as they enter the app)
    updateActivity();

    const interval = setInterval(() => {
      const lastActivity = getLastActivity();
      const now = Date.now();
      const diff = Math.floor((now - lastActivity) / 1000);

      logger.log(`Idle check: ${diff}s since last activity (limit: ${idleTimeout}s)`);

      if (now - lastActivity > IDLE_TIMEOUT) {
        logger.warn('Idle timeout reached! Locking session.');
        setIsV3Expired(true);
      }
    }, CHECK_INTERVAL);

    return () => {
      logger.log('Idle timeout effect cleaning up');
      events.forEach(event => document.removeEventListener(event, updateActivity));
      clearInterval(interval);
    };
  }, [user, isV3Expired, setIsV3Expired, idleTimeout]);

  // Synchronize unlock across tabs
  useEffect(() => {
    const syncUnlock = (e: StorageEvent) => {
      if (e.key === 'cincel_session_unlocked') {
        setIsV3Expired(false);
        // Refresh activity too to prevent immediate re-lock
        getStorage().setItem('cincel_last_activity', Date.now().toString());
      }
    };

    window.addEventListener('storage', syncUnlock);
    return () => window.removeEventListener('storage', syncUnlock);
  }, [setIsV3Expired]);

  const removeSession = () => {
    clearSession();




    clean();
    router.history.push('/login');
  };

  useEffect(() => {
    if (userTeams) {
      setTeams(userTeams.data);
    }
  }, [userTeams]);

  useEffect(() => {
    if (isLimitsError && limitsError) {
      console.warn('Legacy account limits failed', limitsError);
      // Deprecated endpoint failure should not log out the user
    }

    if (accountLimits && accountLimits?.payload) {
      const { payload } = accountLimits;

      const count = payload.count_documents_vobo_nom151;
      const max = payload.max_documents_vobo_nom151;

      if (max > count) {
        setShowBasicCertificates(true);
      } else {
        setShowBasicCertificates(false);
      }

      if (!accountLimits.ok && accountLimits.errors) {
        console.warn('Legacy account limits OK=false', accountLimits.errors);
      }

      if (accountLimits.ok && accountLimits.payload) {
        setAccountLimits(accountLimits.payload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountLimits, isLimitsError, limitsError]);

  useEffect(() => {
    if (isError && error) {
      console.warn('Legacy subscription failed', error);
      // Deprecated endpoint failure should not log out the user
    }

    if (accountSubscription && accountLimits) {
      if (!accountSubscription.ok && accountSubscription.errors) {
        console.warn('Legacy subscription OK=false', accountSubscription.errors);
      }

      if (
        accountSubscription.currentActivePlan &&
        accountLimits.ok &&
        accountLimits.payload
      ) {
        const totalSize = convertSize(accountLimits.payload.max_storage);
        if (totalSize === '100 GB') {
          setCurrentPlan({
            ...accountSubscription.currentActivePlan,
            name: 'start',
          });
        } else {
          setCurrentPlan(accountSubscription.currentActivePlan);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSubscription, isError, error, accountLimits]);

  useEffect(() => {
    if (user !== null) refetchAccountLimits();
  }, [currentOrganization, refetchAccountLimits, user]);

  useEffect(() => {
    if (user !== null) refetchCredits();
  }, [team?.uuid, refetchCredits, user]);





  const handleLogout = () => {
    removeSession();
  };

  const isFullHeight =
    router.pathname === '/' ||
    router.match.path ===
    '/teams/:teamId/folders/:folderId/document/:documentId';

  return (
    <div className="flex flex-col h-screen overflow-hidden">

      <div className="flex flex-1 overflow-hidden relative">
        <Sidebar
          logo={logo || lightLogo}
          primaryColor={color}
          handleLogout={handleLogout}
          isOpen={mobileSidebarOpen}
          setIsOpen={setMobileSidebarOpen}
        />

        <div
          className={classNames(
            !isFullHeight &&
            'relative flex flex-1 flex-col justify-between overflow-hidden',
            isFullHeight &&
            'relative flex flex-1 flex-col justify-between lg:h-screen lg:max-h-screen lg:overflow-y-hidden',
          )}
        >
          <NavBar
            logo={logo || lightLogo}
            setIsOpen={setMobileSidebarOpen}
          />
          {/* Header */}
          <div
            className={classNames(
              !isFullHeight &&
              'max-w-screen flex-1 overflow-y-auto overflow-x-hidden pt-0 lg:pt-0',
              isFullHeight &&
              'max-w-screen flex-1 overflow-x-hidden pt-0 lg:overflow-y-hidden lg:pt-0',
            )}
          >
            <div
              className={classNames(
                !isFullHeight &&
                'mx-auto mb-4 px-0 pt-0 md:px-6 md:pt-0 lg:mt-0 lg:px-16 lg:py-8',
                isFullHeight &&
                'mx-auto max-w-[100vw] px-0 pt-0 md:px-3 md:pt-0 lg:mt-0 lg:pl-6 lg:pr-1 lg:py-8',
              )}
            >
              <DndProvider backend={HTML5Backend}>
                <div className={className}>{children}</div>
              </DndProvider>
            </div>
          </div>
        </div>
        <PoweredByCincel
          bannerContent={
            <Trans i18nKey="common.about.banner">
              <A
                isExternal
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  position: 'relative',
                  paddingX: '4px',
                  color: 'white',
                  textDecoration: 'none',
                  _hover: { textDecoration: 'underline' },
                }}
                className="text-xs font-medium"
              />
            </Trans>
          }
        />


      </div>
      <GuestLoginModal
        isOpen={isV3Expired}
        onClose={() => setIsV3Expired(false)}
        email={user?.email || guestUser?.email || ''}
        name={user?.name || guestUser?.name || user?.email?.split('@')[0] || 'Usuario'}
        mode="RECOVERY"
        onLogout={handleLogout}
      />
    </div>
  );
}
